// src/components/Cheque/ChequeTable.jsx
// Component to display cheque data in a table format

import React from 'react';

/**
 * ChequeTable Component
 * Displays a list of cheques from cash movements in a table format
 * 
 * @param {Array} cheques - Array of cheque objects from cash movements
 * @param {Function} onEdit - Callback function when edit button is clicked
 * @param {Function} onDelete - Callback function when delete button is clicked
 * @param {boolean} isLoading - Whether data is currently loading
 */
const ChequeTable = ({ cheques, onEdit, onDelete, isLoading }) => {
  /**
   * Format date for display
   * @param {string} dateString - Date string from backend
   */
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  /**
   * Handle edit button click
   * @param {Object} cheque - The cheque item to edit
   */
  const handleEdit = (cheque) => {
    if (onEdit) {
      onEdit(cheque);
    }
  };

  /**
   * Handle delete button click
   * @param {Object} cheque - The cheque item to delete
   */
  const handleDelete = (cheque) => {
    if (onDelete) {
      onDelete(cheque);
    }
  };

  // Show loading state while data is being fetched
  if (isLoading) {
    return (
      <div className="inventory-data-table-container">
        <table className="inventory-data-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Inventory</th>
              <th>Amount</th>
              <th>Reference</th>
              <th>Description</th>
              <th>Sales Person</th>
              <th>Date</th>
              <th>Created By</th>
              {/* <th>Actions</th> */}
            </tr>
          </thead>
          <tbody>
            <tr>
              <td colSpan="8" className="cheque-loading">
                Loading cheque data...
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  }

  // Show empty state if no cheques match filter
  if (!cheques || cheques.length === 0) {
    return (
      <div className="inventory-data-table-container">
        <table className="inventory-data-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Inventory</th>
              <th>Amount</th>
              <th>Reference</th>
              <th>Description</th>
              <th>Sales Person</th>
              <th>Date</th>
              <th>Created By</th>
              {/* <th>Actions</th> */}
            </tr>
          </thead>
          <tbody>
            <tr>
              <td colSpan="8" className="cheque-empty-state">
                No cheque records found.
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  }

  // Render the cheque table with data
  return (
    <div className="inventory-data-table-container">
      <table className="inventory-data-table">
        <thead>
          <tr>
            <th>ID</th>
            <th>Inventory</th>
            <th>Amount</th>
            <th>Reference</th>
            <th>Description</th>
            <th>Sales Person</th>
            <th>Date</th>
            <th>Created By</th>
            {/* Uncomment if you want to enable edit/delete actions */}
            {/* <th>Actions</th> */}
          </tr>
        </thead>
        <tbody>
          {cheques.map((cheque) => (
            <tr key={cheque.id}>
              {/* Display cheque ID (shortened for readability) */}
              <td title={cheque.id}>
                {cheque.id.substring(0, 8)}...
              </td>
              
              {/* Display inventory name */}
              <td>{cheque.inventory || '-'}</td>
              
              {/* Display formatted amount */}
              <td className="amount-cell">
                {cheque.formattedAmount || 
                 (cheque.amount ? `Rs.${parseFloat(cheque.amount).toLocaleString()}` : '-')}
              </td>
              
              {/* Display reference */}
              <td>{cheque.reference || '-'}</td>
              
              {/* Display description */}
              <td className="description-cell" title={cheque.description}>
                {cheque.description || '-'}
              </td>
              
              {/* Display sales person */}
              <td>{cheque.salesPerson || '-'}</td>
              
              {/* Display date */}
              <td>{formatDate(cheque.date)}</td>
              
              {/* Display created by */}
              <td>{cheque.createdBy || '-'}</td>
              
              {/* Uncomment if you want to enable edit/delete actions */}
              {/* <td className="actions-cell">
                <button
                  className="btn-edit"
                  onClick={() => handleEdit(cheque)}
                  title="Edit cheque"
                >
                  Edit
                </button>
                <button
                  className="btn-delete"
                  onClick={() => handleDelete(cheque)}
                  title="Delete cheque"
                >
                  Delete
                </button>
              </td> */}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default ChequeTable;